import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getSelection, COMMAND_PRIORITY_HIGH, KEY_DOWN_COMMAND, KEY_TAB_COMMAND, LexicalEditor } from 'lexical'
import { useEffect, useRef } from 'react'
import { SuggestionListItem } from '../../type'
import { useSpEditorProvider } from '../SpEditorProvider'
import { nodeRegistry, NodeType } from '../nodes/NodeCore'
import { $insertNode1, getCurrentNode, insertPageBreakOrNode, isCurrentNodeEmpty } from '../utils/NodeUtils'
import { TSuggestionProps } from './SpSuggestionPlugin'
type ShortCutProps = {
    editor: LexicalEditor
    event: KeyboardEvent
}
const handleShortCutKeyPress = ({ editor, event, type }: ShortCutProps & { type: NodeType }) => {
    editor.update(() => {
        insertPageBreakOrNode(type)
    })
}
function handleSuggestionKeyPress(
    event: KeyboardEvent,
    editor: LexicalEditor,
    suggestionPropsRef: React.RefObject<TSuggestionProps<SuggestionListItem>>,
    hideSuggestion: () => void,
    keyBuffer: React.RefObject<string>
): boolean {
    const _suggestionPropsRefCurrent = suggestionPropsRef.current

    if (event.key.toLowerCase() === 'escape') {
        event.preventDefault()
        hideSuggestion()
        return true
    }
    if (!/^\d$/.test(event.key)) return false
    event.preventDefault()
    keyBuffer.current += event.key

    clearTimeout((keyBuffer as any).timeout)

    const commit = (code: string) => {
        const index = Number.parseInt(code, 10) - 1
        if (index < _suggestionPropsRefCurrent.options.length) {
            const suggestion = _suggestionPropsRefCurrent.options[index]
            if (suggestion) {
                editor.update(() => {
                    $getSelection()?.insertText(suggestion.title)
                })
            }
        }

        hideSuggestion()
    }

    if (keyBuffer.current.length === 2 || _suggestionPropsRefCurrent.options.length < 10) {
        const code = keyBuffer.current
        keyBuffer.current = ''
        commit(code)
    } else {
        ; (keyBuffer as any).timeout = setTimeout(() => {
            const code = keyBuffer.current
            keyBuffer.current = ''
            commit(code)
        }, 500)
    }

    return true
}

export function ShortcutKeyPlugin() {
    const [editor] = useLexicalComposerContext()
    const { suggestionProps, setSuggestionProps } = useSpEditorProvider()
    const suggestionPropsRef = useRef(suggestionProps)
    const keyBuffer = useRef<string>('')

    // Keep ref updated with latest value
    useEffect(() => {
        suggestionPropsRef.current = suggestionProps
    }, [suggestionProps])
    const hideSuggestion = () => {
        setSuggestionProps((prev: TSuggestionProps<SuggestionListItem>) => ({
            ...prev,
            coords: undefined,
            options: [],
        }))
    }
    useEffect(() => {
        const removeKeyDown = editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event) => {
                const _suggestionPropsRefCurrent = suggestionPropsRef.current
                const shouldShowSuggestion =
                    _suggestionPropsRefCurrent.options?.length > 0 &&
                    _suggestionPropsRefCurrent.coords !== undefined

                if (event.ctrlKey) {
                    const type = getElementTypeFromEventKey(event.key)
                    if (type) {
                        event.preventDefault()
                        handleShortCutKeyPress({ editor, event, type })
                        return true
                    }
                }
                if (shouldShowSuggestion) {
                    return handleSuggestionKeyPress(
                        event,
                        editor,
                        suggestionPropsRef,
                        hideSuggestion,
                        keyBuffer
                    )
                }

                return false
            },
            COMMAND_PRIORITY_HIGH
        )

        const removeTab = editor.registerCommand(
            KEY_TAB_COMMAND,
            (event) => {
                let handled = false
                editor.update(() => {
                    const currentNode = getCurrentNode()
                    if (currentNode) {
                        const type = currentNode.getType()
                        const isNodeEmpty = isCurrentNodeEmpty()
                        const strategy = isNodeEmpty ? 'alwaysReplace' : 'alwaysInsert'
                        if (type === NodeType.Character) {
                            event.preventDefault()
                            $insertNode1({ type: NodeType.Dialogue, refNode: currentNode, strategy: strategy })

                            handled = true
                        } else {
                            event.preventDefault()
                            $insertNode1({ type: NodeType.Character, refNode: currentNode, strategy: strategy })
                            handled = true
                        }
                    }
                })
                return handled
            },
            COMMAND_PRIORITY_HIGH
        )

        return () => {
            removeTab()
            removeKeyDown()

        }
    }, [editor])

    return null
}

/// Returns the type of the node based on the event key pressed.
/// '1' => NodeType.Scene
/// '2' => NodeType.Action
/// '3' => NodeType.Character etc
/// Defined in `shortcut ` field of each node class
const getElementTypeFromEventKey = (key: string): NodeType | undefined => {
    return Object.entries(nodeRegistry).find(
        ([, NodeClass]) => NodeClass.shortcut[1].toLowerCase() == key.toLowerCase()
    )?.[0] as NodeType | undefined
}
